import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import { ROUTE_SLUGS } from '@/i18n';
import { useSwitch } from '@/context/SwitchContext';

const NAV_KEYS = ['about', 'help', 'how', 'stories', 'blog', 'collaborate', 'contact'];

function pathFor(key, lang) {
  const slug = ROUTE_SLUGS[key][lang];
  return slug ? `/${lang}/${slug}` : `/${lang}`;
}

function computeOther(pathname, lang, otherLang) {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length <= 1) return `/${otherLang}`;
  const slug = parts[1];
  if (slug === 'blog') return `/${otherLang}/blog`;
  const key = Object.keys(ROUTE_SLUGS).find((k) => ROUTE_SLUGS[k][lang] === slug);
  if (key) return pathFor(key, otherLang);
  return `/${otherLang}`;
}

export const Header = ({ lang }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { override } = useSwitch();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const otherLang = lang === 'it' ? 'en' : 'it';

  useEffect(() => { setOpen(false); }, [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const switchTo = override || computeOther(location.pathname, lang, otherLang);

  const isActive = (key) => {
    const p = pathFor(key, lang);
    if (key === 'blog') return location.pathname.startsWith(`/${lang}/blog`);
    return location.pathname === p;
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 bg-beige/95 backdrop-blur-sm border-b transition-colors duration-300 ${scrolled ? 'border-terracotta/25' : 'border-transparent'}`}
    >
      <div className="max-w-[1280px] mx-auto px-5 md:px-10 h-[74px] flex items-center justify-between gap-4">
        <Link to={`/${lang}`} data-testid="header-logo" className="flex items-center gap-3 group">
          <img
            src="/logo-header.png"
            alt="In Buona Compagnia"
            className="h-12 md:h-14 w-auto object-contain transition-transform duration-500 group-hover:rotate-[-1.5deg] group-hover:scale-[1.02]"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-0.5 xl:gap-1">
          {NAV_KEYS.map((key) => (
            <Link
              key={key}
              to={pathFor(key, lang)}
              data-testid={`nav-${key}`}
              className={`px-2.5 xl:px-3.5 py-2 rounded-full text-[0.88rem] xl:text-[0.95rem] font-medium whitespace-nowrap transition-colors duration-200 ${isActive(key) ? 'text-terracotta' : 'text-textDark/80 hover:text-terracotta'}`}
            >
              {t(`nav.${key}`)}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div data-testid="language-switcher" className="flex items-center rounded-full border border-terracotta/30 overflow-hidden text-sm font-semibold">
            <button
              data-testid="lang-it"
              onClick={() => navigate(lang === 'it' ? location.pathname : switchTo)}
              className={`px-3 py-1.5 transition-colors ${lang === 'it' ? 'bg-terracotta text-beige' : 'text-textDark/70 hover:text-terracotta'}`}
            >IT</button>
            <button
              data-testid="lang-en"
              onClick={() => navigate(lang === 'en' ? location.pathname : switchTo)}
              className={`px-3 py-1.5 transition-colors ${lang === 'en' ? 'bg-terracotta text-beige' : 'text-textDark/70 hover:text-terracotta'}`}
            >EN</button>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-11 h-11 flex items-center justify-center rounded-full border border-terracotta/30 text-textDark"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-terracotta/20 bg-beige px-5 py-4">
          <nav className="flex flex-col gap-1">
            {['home', ...NAV_KEYS].map((key) => (
              <Link
                key={key}
                to={pathFor(key, lang)}
                data-testid={`mobile-nav-${key}`}
                className="px-4 py-3 rounded-2xl text-lg font-medium text-textDark hover:bg-rosa transition-colors"
              >
                {t(`nav.${key}`)}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};
